import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, Input } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { GrupoDTO } from '../../../models/grupo.model';
import { SubGrupoCreateDTO, SubGrupoDTO, SubGrupoFormDTO } from '../../../models/subgrupo.model';
import { GrupoService } from '../../../services/grupo/grupo.service';
import { SubGrupoService } from '../../../services/grupo/sub-grupo.service';
import { TableColumn } from '../../../models/shared/table-column.model';

@Component({
  selector: 'app-subgrupo',
  templateUrl: './subgrupo.component.html',
  styleUrl: './subgrupo.component.scss',
  providers: [MessageService],
})
export class SubgrupoComponent implements OnInit, OnDestroy {
  /**
   * Modo EMBUTIDO (Operar Cliente / Área do Contador): opera sob o CONTEXTO do
   * cliente (X-Empresa-Contexto). Esconde o page-header e oculta o excluir
   * (backend não libera DELETE /subgrupo no contexto).
   */
  @Input() embutido = false;

  /** Id da empresa do CLIENTE (só no modo embutido). */
  @Input() empresaContextoId?: number | null;

  checked = false;
  date2: Date | undefined;
  subGrupos: SubGrupoDTO[] = [];
  subGruposOriginal: SubGrupoDTO[] = [];
  grupos: GrupoDTO[] = [];
  form: SubGrupoFormDTO = this.createEmptyForm();
  activeTabIndex: number = 1;
  stateOptions: any[] = [
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false },
  ];

  // Paginacao
  first: number = 0;
  rows: number = 10;
  private _lastRows: number = 10;
  totalRecords: number = 0;
  loading: boolean = true;
  searchNome: string = '';
  lastfield: string = 'id';
  lastSort: string = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // ViewChild para foco no campo de busca (F2)
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  // ViewChild para foco no botao de confirmar exclusao
  @ViewChild('deleteConfirmBtn') deleteConfirmBtnRef!: ElementRef<HTMLButtonElement>;

  // Flag para evitar submit duplicado (double-click)
  submitting = false;

  // Dialog de exclusao
  deleteDialogVisible = false;
  deleteSubGrupoId: string | null = null;

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'subgrupo-visible-columns';

  columns: TableColumn[] = [
    { key: 'codigoSubGrupo', labelKey: 'subgrupo.labels.codigo_subgrupo', visible: true, fixed: true, defaultVisible: true },
    { key: 'grupo', labelKey: 'subgrupo.labels.grupo', visible: true, fixed: false, defaultVisible: true },
    { key: 'nome', labelKey: 'subgrupo.labels.nome', visible: true, fixed: true, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'subgrupo.labels.data_da_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'subgrupo.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  // Objetos de traducao (padrao moderno - tipo literal implicito)
  texts = {
    add_novo_subgrupo: '',
    subtitulo: '',
    dados_subgrupo: '',
    lista_de_subgrupos: '',
    confirmar_exclusao: '',
    confirmacao: '',
    subgrupo_excluido_sucesso: '',
    erro_ao_excluir_subgrupo: '',
    nenhum_subgrupo: '',
    ajuste_filtros: '',
    carregando: '',
    adicione_novo_subgrupo: '',
    aba_cadastro: '',
    aba_lista: ''
  };

  labels = {
    grupos: '', nome: '', codigo: '',
    codigo_subgrupo: '', grupo: '',
    data_da_inclusao: '', acoes: '',
    itens_por_pagina: ''
  };

  placeholders = {
    selecione: '', nome_do_estado: '',
    codigo_fatura_eletronica: '', pesquisar: ''
  };

  botoes = {
    adicionar: '', editar: '', excluir: '',
    confirmar: '', cancelar: '',
    salvar: '', limpar: ''
  };

  tooltips = {
    editar: '', excluir: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private messageService: MessageService,
    private subgrupoService: SubGrupoService,
    private grupoService: GrupoService,
    private router: Router,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.loadColumnPreferences();
    this.loadTranslations();
    this.findSubGrupo();
    this.findGrupos();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // =========================================================================
  // Traducoes
  // =========================================================================

  onTabChange(event: { index: number }): void {
    if (event.index === 1) {
      this.texts.add_novo_subgrupo = this.translate.instant('subgrupo.texts.titulo_criar');
      this.texts.subtitulo = this.translate.instant('subgrupo.texts.subtitulo_lista');
    } else {
      const isEdicao = !!this.form.id;
      this.texts.add_novo_subgrupo = this.translate.instant(
        isEdicao ? 'subgrupo.texts.titulo_editar' : 'subgrupo.texts.titulo_criar'
      );
      this.texts.subtitulo = this.translate.instant(
        isEdicao ? 'subgrupo.texts.subtitulo_editar' : 'subgrupo.texts.subtitulo_criar'
      );
    }
  }

  private loadTranslations(): void {
    const isEdicao = !!this.form.id;
    const isListaAtiva = this.activeTabIndex === 1;
    this.texts = {
      add_novo_subgrupo: this.translate.instant(
        isListaAtiva ? 'subgrupo.texts.titulo_criar' : isEdicao ? 'subgrupo.texts.titulo_editar' : 'subgrupo.texts.titulo_criar'
      ),
      subtitulo: this.translate.instant(
        isListaAtiva
          ? 'subgrupo.texts.subtitulo_lista'
          : isEdicao ? 'subgrupo.texts.subtitulo_editar' : 'subgrupo.texts.subtitulo_criar'
      ),
      dados_subgrupo: this.translate.instant('subgrupo.texts.dados_subgrupo'),
      lista_de_subgrupos: this.translate.instant('subgrupo.texts.lista_de_subgrupos'),
      confirmar_exclusao: this.translate.instant('subgrupo.texts.confirmar_exclusao'),
      confirmacao: this.translate.instant('subgrupo.texts.confirmacao'),
      subgrupo_excluido_sucesso: this.translate.instant('subgrupo.texts.subgrupo_excluido_sucesso'),
      erro_ao_excluir_subgrupo: this.translate.instant('subgrupo.texts.erro_ao_excluir_subgrupo'),
      nenhum_subgrupo: this.translate.instant('subgrupo.texts.nenhum_subgrupo'),
      ajuste_filtros: this.translate.instant('subgrupo.texts.ajuste_filtros'),
      carregando: this.translate.instant('subgrupo.texts.carregando'),
      adicione_novo_subgrupo: this.translate.instant('subgrupo.texts.adicione_novo_subgrupo'),
      aba_cadastro: this.translate.instant('subgrupo.texts.aba_cadastro'),
      aba_lista: this.translate.instant('subgrupo.texts.aba_lista')
    };

    this.labels = {
      grupos: this.translate.instant('subgrupo.labels.grupos'),
      nome: this.translate.instant('subgrupo.labels.nome'),
      codigo: this.translate.instant('subgrupo.labels.codigo'),
      codigo_subgrupo: this.translate.instant('subgrupo.labels.codigo_subgrupo'),
      grupo: this.translate.instant('subgrupo.labels.grupo'),
      data_da_inclusao: this.translate.instant('subgrupo.labels.data_da_inclusao'),
      acoes: this.translate.instant('subgrupo.labels.acoes'),
      itens_por_pagina: this.translate.instant('subgrupo.labels.itens_por_pagina')
    };

    this.placeholders = {
      selecione: this.translate.instant('subgrupo.placeholders.selecione'),
      nome_do_estado: this.translate.instant('subgrupo.placeholders.nome_do_estado'),
      codigo_fatura_eletronica: this.translate.instant('subgrupo.placeholders.codigo_fatura_eletronica'),
      pesquisar: this.translate.instant('subgrupo.placeholders.pesquisar')
    };

    this.botoes = {
      adicionar: this.translate.instant('subgrupo.botoes.adicionar'),
      editar: this.translate.instant('subgrupo.botoes.editar'),
      excluir: this.translate.instant('subgrupo.botoes.excluir'),
      confirmar: this.translate.instant('subgrupo.botoes.confirmar'),
      cancelar: this.translate.instant('subgrupo.botoes.cancelar'),
      salvar: this.translate.instant('subgrupo.botoes.salvar'),
      limpar: this.translate.instant('subgrupo.botoes.limpar')
    };

    this.tooltips = {
      editar: this.translate.instant('subgrupo.tooltips.editar'),
      excluir: this.translate.instant('subgrupo.tooltips.excluir')
    };
  }

  // =========================================================================
  // Dados
  // =========================================================================

  findSubGrupo(
    page: number = 0,
    size: number = this.rows,
    sort: string = 'id,asc'
  ) {
    this.loading = true;
    this.subgrupoService
      .get('/subgrupo', { page, size, sort })
      .subscribe({
        next: (response) => {
          this.subGruposOriginal = response.content || [];
          this.subGrupos = [...this.subGruposOriginal];
          this.totalRecords = response.totalElements;
          this.first = page * size;
          this.loading = false;

          if (this.searchNome && this.searchNome.trim().length > 0) {
            this.filtrarLocal(this.searchNome.trim());
          }
        },
        error: () => {
          this.subGruposOriginal = [];
          this.subGrupos = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      });
  }

  findGrupos(): Promise<void> {
    return new Promise((resolve) => {
      this.grupoService.get('/grupo?listarTodos=true').subscribe((response) => {
        this.grupos = response.content;
        resolve();
      });
    });
  }

  // =========================================================================
  // Paginacao
  // =========================================================================

  onPageChange(event: any) {
    const page = event.page;
    const size = event.rows;
    this.findSubGrupo(page, size, `${this.lastfield},${this.lastSort}`);
  }

  onRowsPerPageChange() {
    if (this.rows === this._lastRows) return;
    this._lastRows = this.rows;
    this.first = 0;
    this.findSubGrupo(0, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  // =========================================================================
  // Sort customizado (padrao clientes)
  // =========================================================================

  customSort(event: SortEvent): void {
    if (!event.field || !event.data) return;

    const field = event.field;
    const order = event.order ?? 1;

    this.lastfield = field;
    this.lastSort = order === 1 ? 'asc' : 'desc';

    event.data.sort((a: any, b: any) => {
      const valueA = this.getSortValue(a, field);
      const valueB = this.getSortValue(b, field);

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return -1 * order;
      if (valueB == null) return 1 * order;

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return valueA.localeCompare(valueB) * order;
      }

      return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * order;
    });
  }

  private getSortValue(item: any, field: string): any {
    // Campo virtual: ordenar pelo nome concatenado dos grupos
    if (field === 'grupoNome') {
      return this.getGrupoNome(item)?.toLowerCase() || null;
    }

    // Campo de data: converter para timestamp para ordenacao correta
    if (field === 'criadoEm') {
      const val = item[field];
      if (val == null) return null;
      const ts = new Date(val).getTime();
      return isNaN(ts) ? null : ts;
    }

    const parts = field.split('.');
    let value: any = item;
    for (const part of parts) {
      if (value == null) return null;
      value = value[part];
    }
    if (value != null && typeof value === 'object' && 'nome' in value) {
      return value.nome;
    }
    return value;
  }

  // =========================================================================
  // Busca local
  // =========================================================================

  onSearchChange(value: string): void {
    this.searchNome = value || '';
    if (this.searchNome.trim().length > 0) {
      this.first = 0;
      this.filtrarLocal(this.searchNome.trim());
    } else {
      this.subGrupos = [...this.subGruposOriginal];
      this.totalRecords = this.subGruposOriginal.length;
    }
  }

  private filtrarLocal(term: string): void {
    const termLower = term.toLowerCase();

    this.subGrupos = this.subGruposOriginal.filter(item => {
      if (this.isColumnVisible('codigoSubGrupo') && item.codigoSubGrupo != null) {
        if (String(item.codigoSubGrupo).includes(termLower)) return true;
      }
      if (this.isColumnVisible('grupo') && item.grupos?.length) {
        const grupoNome = this.getGrupoNome(item);
        if (grupoNome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('nome') && item.nome) {
        if (item.nome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('criadoEm') && item.criadoEm) {
        const dataFormatada = new Date(item.criadoEm).toLocaleDateString('pt-BR');
        if (dataFormatada.toLowerCase().includes(termLower)) return true;
      }
      return false;
    });

    this.totalRecords = this.subGrupos.length;
  }

  // =========================================================================
  // Formulario
  // =========================================================================

  getEmpresaId(): number {
    // No embutido a empresa é a do CLIENTE (contexto), não a do contador (sessão).
    if (this.embutido) return Number(this.empresaContextoId ?? 0);
    return Number(sessionStorage.getItem('id'));
  }

  submit() {
    if (this.submitting) return;

    if (this.form.nome === '' || this.form.nome === null) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('subgrupo.mensagens.campo_nome_obrigatorio'),
      });
      return;
    }

    if (!this.form.grupo_id || this.form.grupo_id.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('subgrupo.mensagens.campo_grupo_obrigatorio'),
      });
      return;
    }

    this.submitting = true;

    const body: SubGrupoCreateDTO = {
      nome: this.form.nome,
      grupo_id: this.form.grupo_id,
      empresaId: this.getEmpresaId()
    };

    this.subgrupoService.post('/subgrupo', body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('subgrupo.mensagens.subgrupo_editado'),
        });
        this.limparForm();
        this.findSubGrupo();
        this.activeTabIndex = 1;
        this.submitting = false;
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  salvarEdicaoDeSubGrupo() {
    if (this.submitting) return;

    if (this.form.nome === '' || this.form.nome === null) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('subgrupo.mensagens.campo_nome_obrigatorio'),
      });
      return;
    }

    if (!this.form.grupo_id || this.form.grupo_id.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('subgrupo.mensagens.campo_grupo_obrigatorio'),
      });
      return;
    }

    this.submitting = true;

    const body: SubGrupoCreateDTO = {
      nome: this.form.nome!,
      grupo_id: this.form.grupo_id,
      empresaId: this.getEmpresaId()
    };

    this.subgrupoService.put(`/subgrupo/${this.form.id}`, body).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: this.translate.instant('common.sucesso'), detail: this.translate.instant('subgrupo.mensagens.subgrupo_editado') });
        this.limparForm();
        this.findSubGrupo();
        this.activeTabIndex = 1;
        this.submitting = false;
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  // =========================================================================
  // Helpers de grupo
  // =========================================================================

  getGrupoIds(row: SubGrupoDTO): string {
    return (row.grupos ?? [])
      .map(g => g.id)
      .filter((v): v is number => v != null)
      .join(', ');
  }

  getGrupoCodigo(row: SubGrupoDTO): string {
    return (row.grupos ?? [])
      .map(g => g.codigoGrupo)
      .filter((v): v is number => v != null)
      .join(', ');
  }

  getGrupoNome(row: SubGrupoDTO): string {
    return (row.grupos ?? [])
      .map(g => g.nome)
      .filter((v): v is string => v != null)
      .join(', ');
  }

  // =========================================================================
  // Exclusao
  // =========================================================================

  deletarSubGrupo(id: string) {
    if (this.embutido) return; // DELETE /subgrupo fora do allowlist do contexto.
    this.deleteSubGrupoId = id;
    this.deleteDialogVisible = true;
  }

  onDeleteDialogShow() {
    setTimeout(() => {
      this.deleteConfirmBtnRef?.nativeElement?.focus();
    }, 50);
  }

  confirmarExclusao() {
    if (!this.deleteSubGrupoId) return;

    this.subgrupoService.delete(`/subgrupo/${this.deleteSubGrupoId}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.texts.confirmacao,
          detail: this.texts.subgrupo_excluido_sucesso,
        });
        this.deleteDialogVisible = false;
        this.deleteSubGrupoId = null;
        this.findSubGrupo();
      },
      error: () => {
        this.deleteDialogVisible = false;
        this.deleteSubGrupoId = null;
      },
    });
  }

  // =========================================================================
  // Formulario helpers
  // =========================================================================

  createEmptyForm(): SubGrupoFormDTO {
    return {
      nome: '',
      grupo_id: [],
    };
  }

  limparForm() {
    this.form = this.createEmptyForm();

    // Voltar titulo para modo criacao
    this.texts.add_novo_subgrupo = this.translate.instant('subgrupo.texts.titulo_criar');
    this.texts.subtitulo = this.translate.instant('subgrupo.texts.subtitulo_criar');
  }

  editarSubGrupo(event: SubGrupoFormDTO, grupoIds: string) {
    const grupoIdsArray = grupoIds.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id));
    this.form = { ...event, grupo_id: grupoIdsArray };

    // Atualizar titulo para modo edicao
    this.texts.add_novo_subgrupo = this.translate.instant('subgrupo.texts.titulo_editar');
    this.texts.subtitulo = this.translate.instant('subgrupo.texts.subtitulo_editar');

    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  novoSubGrupo(): void {
    this.limparForm();
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // =========================================================================
  // Colunas
  // =========================================================================

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }

  // =========================================================================
  // Formatacao
  // =========================================================================

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) return '';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  // =========================================================================
  // Atalhos de teclado
  // =========================================================================

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(ev: KeyboardEvent): void {
    // Atalhos do dialog de exclusao
    if (this.deleteDialogVisible) {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        this.confirmarExclusao();
        return;
      }
      if (ev.key === 'Escape') {
        ev.preventDefault();
        this.deleteDialogVisible = false;
        return;
      }
      return;
    }

    // F2: focar no campo de busca
    if (ev.key === 'F2') {
      ev.preventDefault();
      setTimeout(() => {
        this.searchInputRef?.nativeElement?.focus();
      }, 100);
      return;
    }

    // Ctrl+Enter: adicionar ou salvar subgrupo
    if (ev.ctrlKey && ev.key === 'Enter') {
      ev.preventDefault();
      this.form.id ? this.salvarEdicaoDeSubGrupo() : this.submit();
      return;
    }
  }
}
